import { NgClass, NgIf, NgTemplateOutlet } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormControl, ReactiveFormsModule } from "@angular/forms";
import { NgbModal, NgbTypeahead } from "@ng-bootstrap/ng-bootstrap";
import * as IBAN from "iban";
import { NgxColorsModule } from "ngx-colors";
import { NgxMaskDirective } from "ngx-mask";
import { ChipsModule } from "primeng/chips";
import { IBANService } from "projects/assist/src/app/_shared/form/services/iban-service";
import { Observable, OperatorFunction, debounceTime, distinctUntilChanged, map } from "rxjs";
import { ModalComponent } from "../../../components/layout/modal/modal.component";
import { NoScrollInputDirective } from "../../../directives/no-scroll-input.directive";
import { FormControlElement } from "../../models/form-control-element";
import { validateEmailsArray } from "../../validators/emails";
import { urlValidator } from "../../validators/website";
import { ButtonComponent } from "../button/button.component";
import { HorizontalWrapperComponent } from "../horizontal-wrapper/horizontal-wrapper.component";
import { ValidatorComponent } from "../validator/validator.component";

export type TextInputType =
  | "text"
  | "iban"
  | "color"
  | "password"
  | "date"
  | "number"
  | "email"
  | "vatnumber"
  | "enterprisenumber"
  | "amount"
  | "percent"
  | "website"
  | "action"
  | "phone"
  | "facebook"
  | "instagram"
  | "twitter"
  | "delete"
  | "address"
  | "textarea";

@Component({
  selector: "app-horizontal-text-input",
  templateUrl: "./horizontal-text-input.component.html",
  styleUrls: ["./horizontal-text-input.component.scss"],
  standalone: true,
  imports: [
    HorizontalWrapperComponent,
    NgIf,
    NgClass,
    ReactiveFormsModule,
    NgbTypeahead,
    NgxColorsModule,
    ChipsModule,
    NgxMaskDirective,
    ValidatorComponent,
    ButtonComponent,
    NgTemplateOutlet,
    NoScrollInputDirective,
    ModalComponent,
  ],
})
export class HorizontalTextInputComponent extends FormControlElement implements OnInit, OnChanges {
  @Input() label: string = "";
  @Input() placeholder: string = "";
  @Input() type: TextInputType = "text";
  @Input() decimals: number = 2;
  @Input() col3: boolean = false;
  @Input() col4: boolean = false;
  @Input() alignRight: boolean = false; // het label rechts uitlijnen
  @Input() id: string = "";
  @Input() isOrganisation: boolean = false;
  @Input() index: number = 0; //index voor als het in een array gebruikt word.
  @Input() href: string = "";
  @Input() typeahead!: string[];
  @Input() iconClass: string = "";
  @Input() actionTitle: string = "";
  @Input() isLoading: boolean = false;
  @Input() isReadOnly: boolean = false;
  @Input() inTable: boolean = false;
  @Input() addMinValidator: boolean = true;
  @Input() addPrefix: boolean = true;
  @Input() contentAlignRight: boolean = false;
  @Input() noCols: boolean = false;
  @Input() textareaRows: number = 5;
  @Input() showCopy: boolean = false;

  @Input() modal: boolean = false; // toon modal met meer info
  @Input() modalTitle: string = "Meer info"; // titel van de modal
  @Input() modalInfo!: HTMLDivElement; //  inhoud van de modal  dead code?
  @ViewChild("content") content!: ElementRef;

  @Output() deleteEvent = new EventEmitter<{ index: number; text: string }>(); // functie om de keuze te verwijderen
  @Output() actionEvent = new EventEmitter<{ index: number; text: string }>();
  @Output() blur = new EventEmitter<{ index: number; text: string }>();

  copied: boolean = false;
  showPassword: boolean = false;
  emails: any;
  //formcontrol with email validator
  emailControl = this.fb
    .group({
      email: ["", validateEmailsArray],
    })
    .get("email") as FormControl;

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder
  ) {
    super();
  }

  search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map((term) =>
        term.length < 2
          ? []
          : this.typeahead
              .filter((v) => (v ? v.toLowerCase().indexOf(term.toLowerCase()) > -1 : false))
              .slice(0, 10)
      )
    );

  ngOnInit(): void {
    if (this.type == "amount") {
      this.control.patchValue(this.formatAmount(this.control.value), {
        emitEvent: false,
      });

      this.control.valueChanges.pipe(debounceTime(3000)).subscribe((x) => {
        var number = +x;
        var string = number.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: this.decimals,
          useGrouping: false,
        });
        this.control.patchValue(string, { emitEvent: false });
      });
    }

    if (this.type == "color") {
      this.control.valueChanges.subscribe((value) => {
        if (value && value.startsWith("#")) {
          this.control.setValue(value, { emitEvent: false });
        }
      });
    }

    if (this.type == "email") {
      if (this.control.value) this.setEmail(this.control.value);
      //if value contains a comma, split it and add it to the emails array otherwise add it to the array
      this.control.valueChanges.subscribe((value) => {
        if (value) this.setEmail(value);
      });

      this.emailControl.valueChanges.subscribe((value) => {
        this.emails = value;
        let concattedEmails = "";
        if (Array.isArray(this.emails)) {
          concattedEmails = this.emails.join(";");
        } else {
          concattedEmails = this.emails;
        }
        this.control.setValue(concattedEmails, { emitEvent: false });
        // Propageer emailControl errors naar de originele control
        if (this.emailControl.errors) {
          this.control.setErrors(this.emailControl.errors);
        } else {
          this.control.setErrors(null);
        }
      });

      // Initiële validatie uitvoeren
      this.emailControl.updateValueAndValidity();
      if (this.emailControl.errors) {
        this.control.setErrors(this.emailControl.errors);
      }

      if (this.control.enabled) this.emailControl.enable({ emitEvent: false });
      else this.emailControl.disable({ emitEvent: false });
    }

    if (this.type == "iban") {
      this.control.addValidators(IBANService.validateIban);

      this.control.valueChanges.subscribe((value) => {
        if (this.control.valid) {
          this.control.patchValue(IBAN.printFormat(value, " "), {
            emitEvent: false,
          });
        }
      });
    }

    if (this.type == "website") {
      this.trimUrl(this.control.value);
      this.control.addValidators(urlValidator);
      this.control.valueChanges.subscribe((value: string) => {
        this.trimUrl(value);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["control"]) {
      this.ngOnInit();
    }
  }

  trimUrl(url: string) {
    if (url) {
      url = url.trim();
      if (url.startsWith("https://")) {
        this.control.patchValue(url.substring(8));
      }
      if (url.startsWith("http://")) {
        this.control.patchValue(url.substring(7));
      }
    }
  }

  setEmail(value: string) {
    //remove spaces
    //value = value.replace(/\s/g, '');
    if (value.includes(",") || value.includes(";") || value.includes(" ")) {
      this.emails = value.split(",").join(";").split(";").join(" ").split(" ");
      //remove all empty strings
      this.emails = this.emails.filter((email: string) => email != "");
      this.emailControl.setValue(this.emails, { emitEvent: false });
    } else {
      if (value && value.length && value.length > 0) this.emails = [value];
      this.emailControl.setValue(this.emails, { emitEvent: false });
    }
  }

  open(content: any) {
    this.modalService.open(content, {
      ariaLabelledBy: "modal-basic-title",
      centered: true,
      size: "lg",
    });
  }

  delete() {
    if (this.deleteEvent) this.deleteEvent.emit({ index: this.index, text: this.control.value });
  }
  doAction() {
    if (this.actionEvent) this.actionEvent.emit({ index: this.index, text: this.control.value });
  }
  formatAmount(amount: number) {
    if (amount != undefined && amount != null)
      return amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: this.decimals,
        useGrouping: false,
      });
    return amount;
  }
  onBlur() {
    if (this.blur) this.blur.emit({ index: this.index, text: this.control.value });
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  copyToClipboard() {
    if (this.control.value) {
      navigator.clipboard.writeText(this.control.value).then(() => {
        this.copied = true;
        setTimeout(() => (this.copied = false), 3000);
      });
    }
  }
}
